import { Component, computed, inject, input } from '@angular/core';
import { CommonAssociationCardContentComponent } from '../association-card';
import {
  CrmDependencies,
  CrmInjectionToken,
  crmObjectDependenciesTokenGenerator,
  CrmObjectInjectionToken,
  ObjectType,
} from '../../../tokens-and-interfaces';
import { Association, CrmObjectDisplayService, CustomObjectTableCustomizationService } from '../../../shared-services';
import { CrmObjectAssociationPanelComponent } from '../crm-object-association-panel/crm-object-association-panel.component';
import { AssociationCardData, CardAction } from '../crm-object-association-panel/tokens-and-interfaces';
import { firstValueFrom, map } from 'rxjs';
import { CustomObjectType } from '@vendasta/crm';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateForCrmObjectService } from '../../../i18n/translate-for-crm-object/translate-for-crm-object.service';
import { PAGE_ROUTES } from '../../../constants';

const OBJECT_URL_ROOT = 'custom-object';

export interface CustomObjectAssociationCardData extends AssociationCardData {
  customObjectType: string;
}

@Component({
  selector: 'crm-custom-object-association-panel',
  template: `
    <crm-object-association-panel
      [id]="id()"
      [name]="name()"
      [objectType]="objectType()"
      [objectSubtype]="objectSubtype()"
      [associatedObject]="'CustomObject'"
      [associatedObjectCustomType]="associatedObjectCustomType()"
      [createCardData]="cardFunction"
      [customActions]="[editCustomObjectPageAction()]"
    ></crm-object-association-panel>
  `,
  styleUrls: ['../../../shared-styles/association-panel-shared.scss'],
  imports: [CrmObjectAssociationPanelComponent, CommonAssociationCardContentComponent],
  providers: [
    { provide: Window, useValue: window },
    {
      provide: CrmObjectInjectionToken,
      useFactory: (
        component: CustomObjectAssociationPanelComponent,
        tableCustomization: CustomObjectTableCustomizationService,
        crm: CrmDependencies,
      ) => {
        return crmObjectDependenciesTokenGenerator(crm, tableCustomization, OBJECT_URL_ROOT, {
          objectSubtype$: component.associatedObjectCustomType$.pipe(map((a) => a?.customObjectTypeId || '')),
          singularObjectName$: component.associatedObjectCustomType$.pipe(map((a) => a?.singularObjectName || '')),
          pluralObjectName$: component.associatedObjectCustomType$.pipe(map((a) => a?.pluralObjectName || '')),
        });
      },
      deps: [CustomObjectAssociationPanelComponent, CustomObjectTableCustomizationService, CrmInjectionToken],
    },
    TranslateForCrmObjectService,
  ],
})
export class CustomObjectAssociationPanelComponent {
  private readonly config: CrmDependencies = inject(CrmInjectionToken);
  private readonly objectDisplayService = inject(CrmObjectDisplayService);
  private readonly window = inject(Window);
  private readonly clipboard = inject(Clipboard);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translateService = inject(TranslateService);

  id = input('');
  name = input('');
  objectType = input<ObjectType>('CustomObject');
  objectSubtype = input<string>('');
  associatedObject = input.required<ObjectType>();
  associatedObjectCustomType = input<CustomObjectType | null>(null);
  associatedObjectCustomType$ = toObservable<CustomObjectType | null>(this.associatedObjectCustomType);
  customActions = input<CardAction[]>([]);

  editCustomObjectPageAction = computed(() => {
    const associatedCustomType = this.associatedObjectCustomType();
    return {
      label: this.translateService.instant('OBJECT.ASSOCIATION_PANEL.EDIT_OBJECT', {
        singularObjectName: (associatedCustomType?.singularObjectName ?? 'object').toLowerCase(),
      }),
      callback: (data: AssociationCardData) => {
        this.openEditCustomObjectPage(data.objectId || '');
      },
      icon: 'open_in_new',
    } as CardAction;
  });

  async openEditCustomObjectPage(id: string) {
    const routePrefix = await firstValueFrom(this.config.routePrefix$);
    let newPath = `${routePrefix}/${PAGE_ROUTES.CUSTOM_OBJECT.ROOT}/${PAGE_ROUTES.CUSTOM_OBJECT.SUBROUTES.EDIT}`
      .replace(':crmObjectId', id)
      .replace(':customObjectTypeID', this.associatedObjectCustomType()?.customObjectTypeId ?? '');
    newPath = newPath + `?returnUrl=${this.window?.location.pathname}`;
    this.window?.open(newPath, '_blank')?.focus();
  }

  protected readonly cardFunction = (
    associations: Association[],
    routePrefix: string,
  ): CustomObjectAssociationCardData[] => {
    return associations.map((association) => {
      const fullName = this.objectDisplayService.getObjectDisplayName(
        'CustomObject',
        association.associatedObject || {},
      );
      const customObjectType = association?.associatedObject?.crmObjectSubtype || '';
      const crmObjectID = association?.associatedObject?.crmObjectId || '';

      return {
        associationId: association.associationId,
        objectId: crmObjectID,
        name: fullName,
        profileUrl: `${routePrefix}/${PAGE_ROUTES.CUSTOM_OBJECT.ROOT}/${PAGE_ROUTES.CUSTOM_OBJECT.SUBROUTES.PROFILE}`
          .replace(':customObjectTypeID', customObjectType)
          .replace(':crmObjectId', crmObjectID),
        icon: 'business',
        tags: (association.tags || []).filter((tag) => tag !== 'Primary'),
        customObjectType: customObjectType,
      } as CustomObjectAssociationCardData;
    });
  };

  copyToClipboard(email: string): void {
    this.clipboard.copy(email);
    this.snackbarService.openSuccessSnack('COMMON.SNACKBAR_MESSAGE.COPY_SUCCESS', { action: 'Close' });
  }
}
